import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../Ventas.css'; // Utilizando los estilos mejorados
import API from '../../../../utils/api';
import {
  Calendar,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  DollarSign,
  Users,
  Clock,
  ChevronRight,
  CircleCheck,
  List,
  Grid,
  RefreshCw,
  PlusCircle,
  Eye,
  Truck,
  Home,
  Send,
  PackageCheck
} from 'lucide-react';

const PedidosDomicilio = () => {
  const navigate = useNavigate();

  // Estados principales
  const [activeStatusTab, setActiveStatusTab] = useState('en_proceso');
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [viewMode, setViewMode] = useState('tabla'); // 'tabla' o 'grid'
  const [pedidosFiltrados, setPedidosFiltrados] = useState([]);

  // Función para cargar los pedidos según el estado seleccionado
  const fetchPedidos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar que el método API exista
      if (typeof API.pedidos?.getPedidosActivos !== 'function') {
        throw new Error('La función API.pedidos.getPedidosDomicilio no está disponible');
      }
      let tipoFiltro = 'domicilio';

      // Para la tab "en_proceso" necesitamos obtener tanto abiertos como pendientes
      let estadoFiltro = activeStatusTab;
      if (activeStatusTab === 'en_proceso') {
        // Para en_proceso, usamos 'todos' y luego filtramos manualmente
        estadoFiltro = 'todos_dom';
      }

      // Llamar a la API para obtener los pedidos
      const pedidosData = await API.pedidos.getPedidosActivos(tipoFiltro, estadoFiltro);

      // Ordenar por fecha, más recientes primero
      const pedidosOrdenados = pedidosData.sort((a, b) => {
        return new Date(b.fechaCreacion) - new Date(a.fechaCreacion);
      });

      setPedidos(pedidosOrdenados);

      // Filtramos los pedidos según la tab seleccionada
      if (activeStatusTab === 'en_proceso') {
        // Para "En Proceso" mostramos todos los estados excepto entregado
        const filtrados = pedidosOrdenados.filter(pedido =>
          ['pendiente', 'finalizado', 'enviado'].includes(pedido.status)
        );
        setPedidosFiltrados(filtrados);
      } else {
        // Para las demás tabs, usamos todos los pedidos tal cual
        setPedidosFiltrados(pedidosOrdenados);
      }
    } catch (err) {
      console.error("Error al cargar pedidos a domicilio:", err);
      setError("No se pudieron cargar los pedidos. Intente de nuevo.");

      // En caso de error, usar datos de muestra
      const pedidosMuestra = [
        // Aquí puedes agregar datos de muestra si es necesario
      ];

      setPedidos(pedidosMuestra);
      setPedidosFiltrados(pedidosMuestra);
    } finally {
      setLoading(false);
    }
  }, [activeStatusTab]);

  // Efecto para cargar pedidos cuando cambia la pestaña
  useEffect(() => {
    fetchPedidos();
  }, [activeStatusTab, fetchPedidos]);

  // Manejadores de eventos
  const handleStatusTabChange = useCallback((tab) => {
    setActiveStatusTab(tab);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prevMode => prevMode === 'tabla' ? 'grid' : 'tabla');
  }, []);

  const handleNuevoPedido = useCallback(() => {
    // Navegar a nuevo pedido sin localStorage
    navigate('/domicilio-nuevo');
  }, [navigate]);

  const handleContinuarPedido = useCallback((pedido) => {
    const datosPedido = {
      nombreCliente: pedido.cliente?.nombreCliente || pedido.clienteLlevar || '',
      cliente: pedido.cliente?.nombreCliente || pedido.clienteLlevar || '',
      direccion: pedido.cliente?.direccion || pedido.direccion || '',
      telefono: pedido.cliente?.telefono || pedido.telefono || '',
      repartidor: pedido.mesero || pedido.repartidor || pedido.nombreUsuario || '',
      pedidoCreado: pedido,
      tipoServicio: 'domicilio',
      idPedido: pedido.idPedido,
      numeroPedido: pedido.numeroDia,
      esEdicion: true
    };

    // Navegar con datos del pedido en el estado
    navigate('/domicilio-nuevo', { state: { datosPedido } });
  }, [navigate]);

  const handleCambiarEstado = useCallback(async (idPedido, nuevoEstado) => {
    try {
      setLoadingAction(true);

      if (typeof API.pedidos?.cambiarEstadoPedido !== 'function') {
        throw new Error('La función API.pedidos.cambiarEstadoPedido no está disponible');
      }

      await API.pedidos.cambiarEstadoPedido(idPedido, nuevoEstado);
      fetchPedidos();

    } catch (error) {
      console.error("Error al cambiar estado del pedido:", error);
      alert("No se pudo cambiar el estado del pedido. Intente de nuevo.");
    } finally {
      setLoadingAction(false);
    }
  }, [fetchPedidos]);

  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  // Formatear monto como moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  // Obtener color basado en el estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return '#f59e0b';     // Amarillo - En preparación
      case 'finalizado': return '#22c55e';    // Verde - Listo para entregar
      case 'enviado': return '#0ea5e9';       // Azul - En camino
      case 'entregado': return '#3b82f6';     // Azul oscuro - Entregado
      case 'cerrado': return '#3b82f6';       // Azul oscuro - Cerrado
      default: return '#64748b';
    }
  };

  // Obtener icono basado en el estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pendiente': return <Clock size={16} />;      // Reloj - En preparación
      case 'finalizado': return <Home size={16} />;      // Casa - Listo para entregar
      case 'enviado': return <Truck size={16} />;        // Camión - En camino
      case 'entregado':
      case 'cerrado': return <CircleCheck size={16} />;  // Check - Entregado/Cerrado
      default: return null;
    }
  };

  // Renderizar componente
  return (
    <div className="dashboard">
      <div className="dashboard-content">
        <div className="pedidos-status-tabs">
          <button
            className={`status-tab-button ${activeStatusTab === 'en_proceso' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('en_proceso')}
          >
            EN PROCESO
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'pendiente' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('pendiente')}
          >
            PENDIENTES
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'finalizado' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('finalizado')}
          >
            FINALIZADOS
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'enviado' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('enviado')}
          >
            ENVIADOS
          </button>
          <button
            className={`status-tab-button ${activeStatusTab === 'entregado' ? 'active' : ''}`}
            onClick={() => handleStatusTabChange('entregado')}
          >
            ENTREGADOS
          </button>
        </div>

        <div className="tab-content">
          <div className="pedido-controls">
            <button
              className="nuevo-pedido-button"
              onClick={handleNuevoPedido}
            >
              <PlusCircle size={18} />
              Nuevo Pedido a Domicilio
            </button>
            <div className="view-mode-switch">
              <span>Vista:</span>
              <button
                className={`view-mode-button ${viewMode === 'tabla' ? 'active' : ''}`}
                onClick={() => setViewMode('tabla')}
                title="Vista de tabla"
              >
                <List size={18} />
              </button>
              <button
                className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Vista de tarjetas"
              >
                <Grid size={18} />
              </button>
            </div>

            <button
              className="refresh-button"
              onClick={fetchPedidos}
              disabled={loading}
            >
              <RefreshCw size={16} />
              {loading ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading">Cargando pedidos...</div>
            </div>
          ) : pedidosFiltrados.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏠</div>
              <h3>No hay pedidos {activeStatusTab === 'en_proceso' ? 'en proceso' : activeStatusTab}</h3>
              <p>
                {activeStatusTab === 'pendiente' && 'No hay pedidos pendientes en este momento.'}
                {activeStatusTab === 'en_proceso' && 'No hay pedidos en proceso en este momento. (pendiente, finalizado o enviado)'}
                {activeStatusTab === 'finalizado' && 'No hay pedidos finalizados en este momento.'}
                {activeStatusTab === 'enviado' && 'No hay pedidos enviados en este momento.'}
                {activeStatusTab === 'entregado' && 'No hay pedidos entregados en el período actual.'}
              </p>
            </div>
          ) : (
            <>
              {viewMode === 'tabla' && (
                <div className="pedidos-lista">
                  <table className="pedidos-table">
                    <thead>
                      <tr>
                        <th>Número</th>
                        <th>Cliente</th>
                        <th>Dirección</th>
                        <th>Teléfono</th>
                        <th>Repartidor</th>
                        <th>Total</th>
                        <th>Fecha</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedidosFiltrados.map(pedido => (
                        <tr
                          key={pedido.idPedido || pedido.id}
                          className={`pedido-row ${pedido.status} pedido-clickable`}
                          onClick={() => handleContinuarPedido(pedido)}
                        >
                          <td>{pedido.numeroDia}</td>
                          <td>{pedido.cliente?.nombreCliente || pedido.clienteLlevar || 'Sin nombre'}</td>
                          <td className="direccion-cell" title={pedido.cliente?.direccion || pedido.direccion}>
                            {(pedido.cliente?.direccion || pedido.direccion) && (pedido.cliente?.direccion || pedido.direccion).length > 25 ?
                              `${(pedido.cliente?.direccion || pedido.direccion).substring(0, 25)}...` :
                              (pedido.cliente?.direccion || pedido.direccion || 'Sin dirección')}
                          </td>
                          <td>{pedido.cliente?.telefono || pedido.telefono || 'Sin teléfono'}</td>

                          <td>{pedido.mesero || pedido.repartidor || pedido.nombreUsuario || 'Sin asignar11'}</td>
                          <td>{formatCurrency(pedido.total)}</td>
                          <td>{formatDateTime(pedido.fechaCreacion)}</td>
                          <td>
                            <div className="estado-pill" style={{ backgroundColor: getStatusColor(pedido.status) }}>
                              {getStatusIcon(pedido.status)}
                              <span>{pedido.status === 'pendiente' ? 'Pendiente' :
                                pedido.status === 'finalizado' ? 'Finalizado' :
                                  pedido.status === 'enviado' ? 'Enviado' :
                                    pedido.status === 'entregado' ? 'Entregado' :
                                      pedido.status === 'cerrado' ? 'Cerrado' :
                                        'Desconocido'}</span>
                            </div>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="action-buttons-group">
                              <button
                                className="action-button action-button-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleContinuarPedido(pedido);
                                }}
                                title="Ver detalles del pedido"
                              >
                                <Eye size={16} />
                                <span>Ver</span>
                              </button>
                              {pedido.status === 'finalizado' && (
                                <button
                                  className="action-button action-button-send"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCambiarEstado(pedido.idPedido, 'enviado');
                                  }}
                                  title="Marcar como enviado"
                                >
                                  <Send size={16} />
                                  <span>Enviar</span>
                                </button>
                              )}
                              {pedido.status === 'enviado' && (
                                <button
                                  className="action-button action-button-delivered"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCambiarEstado(pedido.idPedido, 'entregado');
                                  }}
                                  title="Marcar como entregado"
                                >
                                  <PackageCheck size={16} />
                                  <span>Entregado</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewMode === 'grid' && (
                <div className="pedidos-grid">
                  {pedidosFiltrados.map(pedido => (
                    <div
                      key={pedido.idPedido || pedido.id}
                      className={`pedido-card ${pedido.status}`}
                      onClick={() => handleContinuarPedido(pedido)}
                    >
                      <div className="pedido-card-header">
                        <div className="pedido-numero">{pedido.numeroDia}</div>
                        <div className="pedido-fecha">{formatDateTime(pedido.fechaCreacion)}</div>
                      </div>

                      <div className="pedido-card-cliente">
                        <strong>{pedido.cliente?.nombreCliente || pedido.clienteLlevar || 'Sin nombre'}</strong>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#64748b',
                          marginTop: '2px'
                        }}>
                          {(pedido.cliente?.telefono || pedido.telefono) && <div>📞 {pedido.cliente?.telefono || pedido.telefono}</div>}
                        </div>
                      </div>

                      <div className="pedido-card-direccion" title={pedido.cliente?.direccion || pedido.direccion}>
                        🏠 {(pedido.cliente?.direccion || pedido.direccion) && (pedido.cliente?.direccion || pedido.direccion).length > 40 ?
                          `${(pedido.cliente?.direccion || pedido.direccion).substring(0, 40)}...` :
                          (pedido.cliente?.direccion || pedido.direccion || 'Sin dirección')}
                      </div>

                      <div className="pedido-card-repartidor">
                        {console.log("pedido.nombreUsuario o pedido.repartidor", pedido)}
                        👤 Repartidor: {pedido.mesero || pedido.repartidor || pedido.nombreUsuario || 'Sin asignar'}
                      </div>

                      <div className="pedido-card-total">
                        <span>{formatCurrency(pedido.total)}</span>
                      </div>

                      <div className="pedido-card-estado">
                        <div className="estado-pill" style={{ backgroundColor: getStatusColor(pedido.status) }}>
                          {getStatusIcon(pedido.status)}
                          <span>{pedido.status === 'pendiente' ? 'Pendiente' :
                            pedido.status === 'finalizado' ? 'Finalizado' :
                              pedido.status === 'enviado' ? 'Enviado' :
                                pedido.status === 'entregado' ? 'Entregado' :
                                  pedido.status === 'cerrado' ? 'Cerrado' :
                                    'Desconocido'}</span>
                        </div>
                      </div>

                      <div className="pedido-card-acciones" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="card-action-button card-action-button-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContinuarPedido(pedido);
                          }}
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                          <span>Ver Detalle</span>
                        </button>
                        {pedido.status === 'finalizado' && (
                          <button
                            className="card-action-button card-action-button-send"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCambiarEstado(pedido.idPedido, 'enviado');
                            }}
                            title="Marcar como enviado"
                          >
                            <Send size={18} />
                            <span>Enviar</span>
                          </button>
                        )}
                        {pedido.status === 'enviado' && (
                          <button
                            className="card-action-button card-action-button-delivered"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCambiarEstado(pedido.idPedido, 'entregado');
                            }}
                            title="Marcar como entregado"
                          >
                            <PackageCheck size={18} />
                            <span>Entregado</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Overlay de carga para acciones */}
      {loadingAction && (
        <div className="modal-overlay loading-overlay">
          <div className="loading-spinner">
            <div className="loading">Procesando...</div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PedidosDomicilio;